import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO, schemas } from "@/components/SEO";
import { ArticleCard } from "./components/ArticleCard";
import { ArrowLeft, TrendingUp, Target } from "lucide-react";

const articles = [
  {
    title: "How to Get Commercial Cleaning Contracts: 7 Proven Outbound Channels",
    excerpt:
      "A complete guide to acquiring commercial cleaning clients through cold outreach, LinkedIn prospecting, property manager partnerships, and local B2B networking.",
    readTime: 9,
    href: "/success-hub/article/how-to-get-commercial-cleaning-contracts",
  },
  {
    title: "Cold Email & Phone Scripts for Commercial Facility Managers",
    excerpt:
      "Word-for-word cold calling and email outreach scripts that get past building gatekeepers and book commercial walkthrough appointments.",
    readTime: 7,
    href: "/success-hub/article/cold-outreach-scripts-facility-managers",
  },
  {
    title: "How to Pitch Commercial Property Management Companies",
    excerpt:
      "How to get on vendor approved lists for commercial property management firms overseeing multiple office buildings and retail centers.",
    readTime: 8,
    href: "/success-hub/article/pitching-commercial-property-managers",
  },
  {
    title: "Commercial Cleaning Proposal Closing Tactics: Closing After Walkthroughs",
    excerpt:
      "Proven sales techniques to close commercial cleaning contracts on the spot or within 48 hours of completing a facility walkthrough.",
    readTime: 7,
    href: "/success-hub/article/commercial-cleaning-proposal-closing-tactics",
  },
  {
    title: "How to Win Government & Municipal Janitorial Bids (RFP / RFQ Guide)",
    excerpt:
      "Navigating government cleaning RFPs, prevailing wage calculations, SAM.gov registration, and public sector bonding requirements.",
    readTime: 9,
    href: "/success-hub/article/government-municipal-janitorial-bidding-rfp",
  },
  {
    title: "The 5-Touch Lead Follow-Up Cadence for Commercial Cleaning Bids",
    excerpt:
      "A multi-channel follow-up sequence (email, SMS, phone, drop-in) that revives stalled commercial cleaning proposals and doubles close rates.",
    readTime: 6,
    href: "/success-hub/article/lead-follow-up-cadence-cleaning-bids",
  },
  {
    title: "Commercial Cleaning Referral Programs: Generating B2B Account Introductions",
    excerpt:
      "How to build automated referral loops with HVAC contractors, carpet cleaners, commercial real estate brokers, and existing clients.",
    readTime: 6,
    href: "/success-hub/article/commercial-cleaning-b2b-referral-systems",
  },
  {
    title: "Overcoming Cleaning Price Objections: When Clients Say 'Your Bid is Too High'",
    excerpt:
      "How to reframe pricing objections, defend your gross margins, and sell on quality reliability without discounting your rates.",
    readTime: 7,
    href: "/success-hub/article/overcoming-pricing-objections-commercial-cleaning",
  },
];

export default function SalesLeadGenPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO
        title="Sales & Lead Generation | Commercial Cleaning Growth Playbooks"
        description="Proven sales strategies for commercial cleaning contractors: cold calling scripts, property manager pitch decks, proposal closing tactics, and RFP bidding."
        keywords={[
          "how to get commercial cleaning contracts",
          "commercial cleaning sales scripts",
          "pitching property managers cleaning",
          "commercial cleaning cold calling",
          "cleaning lead generation",
        ]}
        url="/success-hub/sales-lead-gen"
        structuredData={[
          schemas.createBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Success Hub", url: "/success-hub" },
            { name: "Sales & Lead Generation", url: "/success-hub/sales-lead-gen" },
          ]),
        ]}
      />
      <LandingNavbar />

      {/* Header */}
      <section className="pb-12 pt-28">
        <div className="container mx-auto px-4">
          <Link
            to="/success-hub"
            className="mb-6 inline-flex items-center text-sm text-slate-600 transition-colors hover:text-blue-600"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Success Hub
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-start gap-6"
          >
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-indigo-100">
              <TrendingUp className="h-8 w-8 text-indigo-600" />
            </div>
            <div>
              <h1 className="mb-3 text-3xl font-bold text-slate-900 md:text-4xl">
                Sales & Lead Generation
              </h1>
              <p className="max-w-2xl text-lg text-slate-600">
                Proven outbound acquisition channels, cold outreach scripts for facility managers, property manager pitch frameworks, and proposal closing playbooks.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Articles List */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="space-y-4"
            >
              {articles.map((article, index) => (
                <ArticleCard
                  key={index}
                  title={article.title}
                  excerpt={article.excerpt}
                  category="Sales & Growth"
                  categoryColor="bg-indigo-100 text-indigo-700"
                  readTime={article.readTime}
                  href={article.href}
                />
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
